import { useCallback, useEffect, useRef } from "react";
import {
    saveDraft,
    loadDraft,
    loadAllDraftsMeta,
    clearDraft,
} from "../utils/billingRateDraftStorage";

const DEBOUNCE_MS = 300;

const useBillingRateDraft = () => {
    const groupIdRef = useRef(null);
    const debounceTimer = useRef(null);
    const pendingRef = useRef(null);

    // meta must include groupId — that is the stable key per submission
    const save = useCallback((meta, data, { immediate = false } = {}) => {
        if (!meta.groupId) return false;
        groupIdRef.current = meta.groupId;
        pendingRef.current = { meta, data };
        clearTimeout(debounceTimer.current);
        if (immediate) {
            const ok = saveDraft(meta, data);
            if (ok) pendingRef.current = null;
            return ok;
        }
        debounceTimer.current = setTimeout(() => {
            if (!pendingRef.current) return;
            const ok = saveDraft(pendingRef.current.meta, pendingRef.current.data);
            if (ok) pendingRef.current = null;
        }, DEBOUNCE_MS);
        return true;
    }, []);

    const flush = useCallback(() => {
        clearTimeout(debounceTimer.current);
        if (!pendingRef.current) return false;
        const ok = saveDraft(pendingRef.current.meta, pendingRef.current.data);
        if (ok) pendingRef.current = null;
        return ok;
    }, []);

    useEffect(() => () => flush(), [flush]);

    const clear = useCallback(() => {
        clearTimeout(debounceTimer.current);
        pendingRef.current = null;
        if (groupIdRef.current) {
            clearDraft(groupIdRef.current);
            groupIdRef.current = null;
        }
    }, []);

    // Load a specific draft by groupId and set it as the active one for future saves
    const resume = useCallback((groupId) => {
        groupIdRef.current = groupId;
        return loadDraft(groupId);
    }, []);

    return { saveDraft: save, flushDraft: flush, clearDraft: clear, resumeDraft: resume, loadAllDraftsMeta };
};

export default useBillingRateDraft;
